import mergeWith from 'lodash.mergewith';
import omit from 'lodash.omit';

import { CARD_ELEMENT_TYPE } from '../../../services/cards/constants';
import { isCardElementEditable } from '../../../services/cards/components/AccountCard/helpers';

/**
 * @enum CONTROL_SECTION
 * @type {{ELEMENTS: string, LAYOUT: string, BACKGROUND: string}}
 */
export const CONTROL_SECTION = {
  LOGO: 'LOGO',
  LAYOUT: 'LAYOUT',
  ELEMENTS: 'ELEMENTS',
  BACKGROUND: 'BACKGROUND',
};

export const MAX_Z_INDEX = 1000;

/**
 * Get a card side.
 *
 * @param {Object} options The options.
 * @param {Object} options.card The Card.
 * @param {CARD_SIDE} options.side The CardSideEnum.
 * @returns {null|*}
 */
export const getCardSide = ({ card, side }) => {
  if (!card || !side) {
    return null;
  }

  const cardSideGroup = card.card_side_groups.records.find(
    x => x.side === side,
  );
  const [cardSide] = cardSideGroup.card_sides.records ?? [];

  return cardSide;
};

/**
 * Creates the CardSide UI state.
 *
 * @param {Object} cardSide The CardSide.
 * @returns {Array}
 */
export const getCardSideElementsConfiguration = cardSide => {
  const cardElementConfigurations = cardSide.card_element_configurations;

  return cardSide.card_template.card_elements.map(cardElement => {
    const cardElementConfiguration = cardElementConfigurations.find(
      x => x.card_element.id === cardElement.id,
    );

    return {
      cardElement,
      cardElementConfiguration: mergeWith(
        // TODO [cards] add default static card element configuration
        {},
        cardElementConfiguration,
      ),
      cardSide,
      side: cardSide.card_template.side,
    };
  });
};

/**
 * Returns CardElementConfigurationInput by element type.
 * Designer version.
 *
 * @param {Object} element The element state.
 * @param {Object} config The configuration.
 * @param {Boolean} [editable] If the CardElement is or will be editable.
 * @returns {{sticker_element_configuration: *}|{}|{photo_element_configuration: *}|{rectangle_element_configuration: *}|{text_element_configuration: *}}
 */
export const getCardDesignerCardElementConfigurationInputByElement = (
  element,
  config,
  editable,
) => {
  const { cardElement, cardElementConfiguration } = element;
  const isEditable = (() => {
    if (editable != null) {
      return editable;
    }

    return isCardElementEditable(element);
  })();

  switch (cardElement.type) {
    case CARD_ELEMENT_TYPE.TEXT:
      return {
        // 1. When the element is editable we want the text entered
        // in the designer to be a help placeholder.
        help_placeholder: isEditable
          ? config.text ||
            config.help_placeholder ||
            cardElementConfiguration?.text ||
            cardElementConfiguration?.help_placeholder
          : null,
        text_element_configuration: {
          font_family: cardElementConfiguration?.font_family,
          font_color: cardElementConfiguration?.font_color,
          text_align: cardElementConfiguration?.text_align,
          font_size: cardElementConfiguration?.font_size,
          font_weight: cardElementConfiguration?.font_weight,
          ...omit(config, ['help_placeholder']),
          // 2. And when the text element is not editable, only then do we want
          // the text entered to be a config (i.e. printed).
          text: !isEditable
            ? config.text ||
              cardElementConfiguration?.help_placeholder ||
              cardElementConfiguration?.text
            : null,
        },
      };
    case CARD_ELEMENT_TYPE.PHOTO:
      return {
        photo_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.STICKER:
      return {
        sticker_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.RECTANGLE:
      return {
        rectangle_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.ADDRESS:
      return {
        address_element_configuration: config,
      };
    default:
      return {};
  }
};
